// BaleenCodeFinder — the baleen cheat sheet (user, 8 Oct 2026: "baleen whales do not have a cheat sheet").
// One card per code of the Code document (passive_codebook.json): its reference picture, species, name, what it
// looks like, frequency and duration range, harmonics, similar calls. Filter by species and tonal / pulsed;
// "Fits this call" keeps the codes whose ranges overlap the call box (frequency) and duration (± 50 %).
// Data: public/cheatsheets/baleen.json + public/cheatsheets/baleen/<code>.png (validation/library_starter.py).
import React, { useEffect, useMemo, useState } from 'react';
import { Info } from './info';
import { LibraryExamples, useSectionLibrary } from './Library';

const BASE = `${process.env.PUBLIC_URL || ''}/cheatsheets`;
let sheet = null;
export const loadBaleenSheet = () => {
  if (!sheet) sheet = fetch(`${BASE}/baleen.json`).then((r) => { if (!r.ok) throw new Error(`cheat sheet ${r.status}`); return r.json(); })
    .then((d) => d.codes).catch((e) => { sheet = null; throw e; });
  return sheet;
};
export const baleenImage = (row) => `${BASE}/${row.image.replace(/^cheatsheets\//, '')}`;

const KEYS = ['pygmy right', 'southern right', 'northern right', 'right', 'blue', 'fin', 'sei', 'minke', 'bryde', 'humpback', 'sperm'];
const keyOf = (name) => { const n = String(name || '').toLowerCase().replace('’', "'"); return KEYS.find((k) => n.includes(k)) || null; };
const RIGHT = ['northern right', 'southern right'];
const matchSp = (sp, name) => { const k = keyOf(name); return sp === 'all' || k === sp || (sp === 'right' && RIGHT.includes(k)); };
const range = (r, unit, d = 0) => (Array.isArray(r) && r[0] != null ? (r[0] === r[1] ? `${(+r[0]).toFixed(d)} ${unit}` : `${(+r[0]).toFixed(d)}–${(+r[1]).toFixed(d)} ${unit}`) : '—');

/** does the call (box: t0, t1, f0, f1 in s / Hz) fit the code's ranges? */
export function fits(row, call) {
  if (!call) return true;
  const fr = row.freqHz; const du = row.durationS; const d = call.t1 - call.t0;
  const fOk = !Array.isArray(fr) || fr[0] == null || (call.f1 >= fr[0] * 0.8 && call.f0 <= fr[1] * 1.2);
  const dOk = !Array.isArray(du) || du[0] == null || (d >= du[0] * 0.5 && d <= du[1] * 1.5);
  return fOk && dOk;
}

export default function BaleenCodeFinder({ code, species, call, onUse, library = null }) {
  const libItems = useSectionLibrary(library, 'baleen');
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [sp, setSp] = useState(() => keyOf(species) || 'all');
  const [kind, setKind] = useState('any');
  const [fit, setFit] = useState(false);
  const [big, setBig] = useState(null);
  useEffect(() => { let live = true; loadBaleenSheet().then((r) => { if (live) setRows(r); }).catch((e) => { if (live) setErr(String(e.message || e)); }); return () => { live = false; }; }, []);
  useEffect(() => { const k = keyOf(species); if (k) setSp(k); }, [species]);
  const speciesOpts = useMemo(() => {
    const m = new Map((rows || []).map((r) => [keyOf(r.species), r.species]));
    if ([...m.keys()].some((k) => RIGHT.includes(k))) m.set('right', 'Right whales (northern + southern)');
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);
  const shown = (rows || []).filter((r) => matchSp(sp, r.species) && (kind === 'any' || r.kind === kind) && (!fit || fits(r, call)));
  if (err) return <p className="cr-error">Could not load the cheat sheet ({err}).</p>;
  if (!rows) return <p className="ct-muted hcr-small">Loading the cheat sheet…</p>;
  return (
    <div className="bf">
      <p className="ct-muted hcr-small">Codes of the Code document with a reference picture of each call. Compare the picture, the frequency range and the duration with your call, then use the code. <Info id="baleenSheet" /></p>
      <div className="bf-filters">
        <label>Species<select className="hcr-input" value={sp} onChange={(e) => setSp(e.target.value)}>
          <option value="all">all species</option>{speciesOpts.map(([k, n]) => <option key={k} value={k}>{n}</option>)}</select></label>
        <label>Kind<select className="hcr-input" value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="any">tonal and pulsed</option><option value="tonal">tonal</option><option value="pulsed">pulsed</option></select></label>
        {call && <label className="rv-check hcr-small"><input type="checkbox" checked={fit} onChange={() => setFit(!fit)} /> fits this call ({Math.round(call.f0)}–{Math.round(call.f1)} Hz, {(call.t1 - call.t0).toFixed(2)} s)</label>}
      </div>
      {!shown.length && <p className="ct-muted hcr-small">No code matches. Try “all species” or switch off “fits this call”.</p>}
      <div className="bf-grid">
        {shown.map((r) => (
          <div key={r.code} className={`bf-card ${r.code === code ? 'bf-on' : ''}`}>
            <button type="button" className="bf-img" onClick={() => setBig(r)} aria-label={`Enlarge the reference picture of ${r.code}`}>
              <img src={baleenImage(r)} alt={`Reference spectrogram: ${r.species}, ${r.name}`} loading="lazy" /></button>
            <div className="hcr-row-between"><span className="hcr-mono">{r.code}</span>
              <span className={`hcr-chip ${r.kind === 'pulsed' ? 'cr-wait' : ''}`}>{r.kind || '?'}</span></div>
            <div className="bf-name">{r.species} — {r.name}</div>
            <dl className="bf-dl">
              <dt>Looks like</dt><dd>{r.appearance || '—'}</dd>
              <dt>Frequency</dt><dd>{range(r.freqHz, 'Hz')}</dd>
              <dt>Duration</dt><dd>{range(r.durationS, 's', r.durationS && r.durationS[1] < 1 ? 2 : 1)}</dd>
              <dt>Harmonics</dt><dd>{r.harmonics || '—'}</dd>
              {r.similarTo && <><dt>Similar to</dt><dd>{r.similarTo}</dd></>}
            </dl>
            <LibraryExamples library={library} items={libItems} code={r.code} max={1} />
            {onUse && (r.code === code ? <span className="cr-saved hcr-small">This is the call’s code</span>
              : <button type="button" className="ct-btn" onClick={() => onUse(r.code)}>Use this code</button>)}
          </div>))}
      </div>
      {big && (
        <div className="bf-big" role="dialog" aria-label={`${big.code} reference picture`} onClick={() => setBig(null)}>
          <figure><img src={baleenImage(big)} alt={`Reference spectrogram: ${big.species}, ${big.name}`} />
            <figcaption><span className="hcr-mono">{big.code}</span> {big.species} — {big.name}. {big.function ? `Function: ${big.function}.` : ''} {big.note || ''} <em>(click to close)</em></figcaption></figure>
        </div>)}
    </div>);
}
